/**
 * Calendar math for tasks: month and week grids, and upcoming occurrences of repeating tasks.
 * Dates are ranch-local calendar days (YYYY-MM-DD); nothing here touches time zones, so a task due
 * Friday stays Friday. Pure and shared by the server and the web.
 */
import { addDays } from "../../../contract";
import { addMonths } from "./equipment";

export const REPEAT_RULES = { daily: "Daily", weekly: "Weekly", monthly: "Monthly", yearly: "Yearly" } as const;
export type RepeatRule = keyof typeof REPEAT_RULES;

/** The next occurrence of a repeating date. Months and years clamp to the last day (Jan 31 → Feb 28, Feb 29 → Feb 28). */
export function nextOccurrence(date: string, rule: RepeatRule, steps = 1): string {
  const day = date.slice(0, 10);
  switch (rule) {
    case "daily": return addDays(day, steps);
    case "weekly": return addDays(day, 7 * steps);
    case "monthly": return addMonths(day, steps);
    case "yearly": return addMonths(day, 12 * steps);
  }
}

/**
 * Future occurrences of a repeating task after its current due date, within [from, to]. Each
 * occurrence counts from the original date so month-end tasks don't drift (Jan 31, Feb 28, Mar 31).
 * These are a plan, not tasks: completing the current task is what creates the next one.
 */
export function projectOccurrences(due: string, rule: string | null | undefined, from: string, to: string, max = 62): string[] {
  if (!rule || !(rule in REPEAT_RULES)) return [];
  const out: string[] = [];
  for (let step = 1; out.length < max && step < 2000; step++) {
    const next = nextOccurrence(due, rule as RepeatRule, step);
    if (next > to) break;
    if (next >= from) out.push(next);
  }
  return out;
}

/** 0 = Sunday, 1 = Monday. */
export type WeekStart = 0 | 1;

export function weekday(date: string): number {
  return new Date(`${date.slice(0, 10)}T12:00:00Z`).getUTCDay();
}

export function startOfWeek(date: string, weekStartsOn: WeekStart = 0): string {
  const shift = (weekday(date) - weekStartsOn + 7) % 7;
  return addDays(date, -shift);
}

export function weekDays(date: string, weekStartsOn: WeekStart = 0): string[] {
  const start = startOfWeek(date, weekStartsOn);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

/** Whole weeks covering a month ("YYYY-MM"), padded with days from the months around it. */
export function monthGrid(month: string, weekStartsOn: WeekStart = 0): string[][] {
  const first = `${month.slice(0, 7)}-01`;
  const last = addDays(addMonths(first, 1), -1);
  const weeks: string[][] = [];
  let cursor = startOfWeek(first, weekStartsOn);
  while (cursor <= last) {
    weeks.push(weekDays(cursor, weekStartsOn));
    cursor = addDays(cursor, 7);
  }
  return weeks;
}

export function shiftMonth(month: string, by: number): string {
  return addMonths(`${month.slice(0, 7)}-01`, by).slice(0, 7);
}
